import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import ResizeObserver from 'react-resize-observer';
import {
  animated,
  useSpring,
  useTransition,
} from 'react-spring';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { FixedSizeList as List } from 'react-window';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import TBTooltip from '../../../../shared/components/TBTooltip/TBTooltip';
import SavedWorkoutDetails from './SavedWorkoutDetails';
import {
  setSecondarySideBarDataTag, setIsCloneDragging, fetchSavedWorkoutDetail,
} from '../../ducks/calendarActions';
import searchByRelevance from '../../../../shared/utils/searchByRelevance';

const Wrapper = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  background: white;
  position: relative;
`;

const TitleContainer = styled('div')`
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
`;

const SearchAndIcon = styled('div')`
  display: flex;
  align-items: center;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  background: white;
  overflow: hidden;
  width: 100%;
  min-height: 32px;
`;

const SearchIconContainer = styled('div')`
  padding-left: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 5px;
  height: 40px;
`;

const SearchContainer = styled('input')`
  display: flex;
  border: none;
  background: none;
  padding-left: 5px;
  width: 100%;
  height: 40px;

  ::placeholder {
    font-size: 16px;
    font-family: 'Nunito Sans';
    color: #9E9E9E;
  }
`;

const ExerciseWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow: visible;
`;

const ExerciseItemContainer = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 4px;
  transition: all 250ms ease-in-out;
  ${(props) => props.addBorderTop && 'border-top: 1px solid #EEEEEE'};
  ${(props) => props.addBorderTop && 'padding-top: 12px'};
  :hover {
    cursor: pointer;
  }
`;

const ExerciseNameAndIconWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AnimationWrapper = styled('div')`
  display: flex;
  gap: 16px;
  width: 100%;
  height: 100%;
`;

const MappedComponents = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ExpandedTitlesContainer = styled('div')`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
`;

const HoverTextContainer = styled('div')`
  display: flex;
  :hover {
      cursor: pointer;
      opacity: 0.7;
    }
`;

const TitleText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${(props) => props.maxWidth}px;
`;

const DroppableDiv = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  min-height: 0;
`;

const StyledListWrapper = styled('div')`
  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(136, 136, 136, 0.5);
    border-radius: 3px;
    transition: background-color 0.3s ease;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  :hover::-webkit-scrollbar-thumb {
    background-color: rgba(136, 136, 136, 1);
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(136, 136, 136, 0.5) transparent;
`;

const TemplateShimmerContainer = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 4px;
  transition: all 250ms ease-in-out;
  border-top: 1px solid #EEEEEE;
  padding-top: 12px;
`;

const TemplateShimmerText = styled('div')`
  width: 200px;
  height: 20px;
  border-radius: 4px;
  background: #EEEEEE;
`;

const TemplateShimmerIcon = styled('div')`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #EEEEEE;
`;

const TagColorDot = styled('span')`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #E0E0E0;
  background: ${(props) => props.color || '#FFFFFF'};
  flex-shrink: 0;
`;

const Item = ({ index, style, data }) => {
  const {
    searchedExercises,
    exerciseWrapperRef,
    isCloneDragging,
    draggingCloneIndex,
    sideBarContainerWidth,
    handleTemplateClick,
  } = data;
  const template = searchedExercises[index];
  if (!template) return null;

  return (
    <>
      <Draggable
        key={template.id}
        draggableId={`${template.id}-sideBar`}
        index={index}
      >
        {(draggableProvided) => (
          <div
            ref={draggableProvided.innerRef}
            {...draggableProvided.draggableProps}
            {...draggableProvided.dragHandleProps}
            style={style}
          >
            <ExerciseItemContainer
              onClick={() => handleTemplateClick(template)}
            >
              <ExerciseNameAndIconWrapper>
                <TBTooltip
                  toolTipBackgroundColor='white'
                  tooltipContainerRef={exerciseWrapperRef}
                  textToDisplay={template?.programName}
                  textToHover={template?.programName}
                  addHoverOpacity
                  fontSize='16px'
                  fontWeight={400}
                  toolTipFontSize='13px'
                  toolTipFontWeight={400}
                  textContainerPadding='0px'
                  textContainerMaxWidth={
                    `${sideBarContainerWidth - 100}px`
                  }
                />
              </ExerciseNameAndIconWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={10}
                icon='altrightarrow'
                color='#9E9E9E'
              />
            </ExerciseItemContainer>
          </div>
        )}
      </Draggable>
      {isCloneDragging && draggingCloneIndex === index && (
        <div style={style}>
          <ExerciseItemContainer>
            <ExerciseNameAndIconWrapper>
              <TBTooltip
                toolTipBackgroundColor='white'
                tooltipContainerRef={exerciseWrapperRef}
                textToDisplay={template?.programName}
                textToHover={template?.programName}
                addHoverOpacity
                fontSize='16px'
                fontWeight={400}
                toolTipFontSize='13px'
                toolTipFontWeight={400}
                textContainerPadding='0px'
                textContainerMaxWidth={
                  `${sideBarContainerWidth - 100}px`
                }
              />
            </ExerciseNameAndIconWrapper>
            <IcomoonReact
              iconSet={iconSet}
              size={10}
              icon='altrightarrow'
              color='#9E9E9E'
            />
          </ExerciseItemContainer>
        </div>
      )}
    </>
  );
};

Item.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    searchedExercises: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      programName: PropTypes.string,
    })),
    exerciseWrapperRef: PropTypes.shape({}),
    isCloneDragging: PropTypes.bool,
    draggingCloneIndex: PropTypes.number,
    sideBarContainerWidth: PropTypes.number,
    handleTemplateClick: PropTypes.func,
  }).isRequired,
};

const TemplatesSideBarContent = ({
  windowWidth, sideBarContainerHeight, sideBarContainerWidth,
}) => {
  const dispatch = useDispatch();
  const exerciseWrapperRef = useRef(null);
  const expandedTitlesContainerHeightRef = useRef(null);
  const tagFilterRef = useRef(null);
  const [tagFilterHeight, setTagFilterHeight] = useState(0);
  const [showTagFilterDropdown, setShowTagFilterDropdown] = useState(false);
  const [searchedExercises, setSearchedExercises] = useState([]);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const tagsForAllPrograms = useSelector((state) => state.calendar.data.tagsForAllPrograms);
  const tagOptions = [...tagsForAllPrograms]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((tag) => ({
      ...tag,
      avatar: tag.color && tag.color !== '#FFFFFF'
        ? <TagColorDot color={tag.color} />
        : undefined,
      avatarRight: true,
    }));
  const [containerWidth, setContainerWidth] = useState(0);
  const [expandedTitlesContainerHeight, setExpandedTitlesContainerHeight] = useState(0);
  const [showSecondLayerInfo, setShowSecondLayerInfo] = useState(false);
  const [showThirdLayerInfo, setShowThirdLayerInfo] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [draggingCloneIndex, setDraggingCloneIndex] = useState(null);
  const isCloneDragging = useSelector((state) => state.calendar.data.isCloneDragging);

  useEffect(() => {
    if (!isCloneDragging) {
      setDraggingCloneIndex(null);
    }
  }, [isCloneDragging]);

  const isAllExercisesLoading = useSelector((state) => state.calendar.ui.isAllExercisesLoading);
  const savedWorkouts = useSelector((state) => state.calendar.data.savedWorkouts);
  const selectedSavedWorkout = useSelector((state) => state.calendar.data.selectedSavedWorkout);
  const isLoadingSavedWorkout = useSelector((state) => state.calendar.data.isLoadingSavedWorkout);
  const currentUser = useSelector((state) => state.auth.data.currentUser);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      fontFamily: 'Nunito Sans',
      fontSize: '16px',
      height: '44px',
      fontWeight: 400,
      background: 'white',
      border: '2px solid #EEEEEE',
      boxShadow: state.isFocused ? 'none' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#EEEEEE' : '#EEEEEE',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '4px',
      borderRadius: '4px',
      backgroundColor: 'white',
      color: state.isSelected ? '#BDBDBD' : '#444444',
      fontFamily: 'Nunito Sans',
      fontSize: '16px',
      marginBottom: '8px',
      fontWeight: 400,
      '&:hover': {
        backgroundColor: '#FCFCFC',
      },
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: '#FCFCFC',
      border: '1px solid #EEEEEE',
      borderRadius: '4px',
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: '#444444',
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: '#9E9E9E',
    }),
    menu: (provided) => ({
      ...provided,
      border: '0px',
      minWidth: '250px',
      padding: '16px',
      marginTop: '0px',
      marginBottom: '0px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9E9E9E',
    }),
    input: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? 'inherit' : provided.color, /** Use 'inherit' to maintain original color */
    }),
  };

  useEffect(() => {
    const tagFiltered = tagSearchTerm
      ? (savedWorkouts || []).filter((obj) => obj.tags?.some((tag) => tag.id === tagSearchTerm))
      : savedWorkouts;
    const filteredExercises = searchByRelevance(tagFiltered, searchTerm, (obj) => obj.programName);

    setSearchedExercises(filteredExercises);
  }, [searchTerm, tagSearchTerm, savedWorkouts]);

  useEffect(() => {
    const updateHeight = () => {
      if (expandedTitlesContainerHeightRef.current) {
        const rect = expandedTitlesContainerHeightRef.current.getBoundingClientRect();
        setExpandedTitlesContainerHeight(rect.height);
      }
      setTagFilterHeight(
        tagFilterRef.current ? tagFilterRef.current.getBoundingClientRect().height : 0,
      );
    };
    /** Update height on mount */
    updateHeight();
    /** Optional: Update height on window resize or other triggers */
    window.addEventListener('resize', updateHeight);

    /** Cleanup event listener on unmount */
    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [showSecondLayerInfo, showThirdLayerInfo, showTagFilterDropdown]);

  // const searchedExercises = savedWorkouts?.filter(
  //   (obj) => obj.programName?.toLowerCase().includes(
  //     searchTerm.toLowerCase(),
  //   ),
  // ) || [];

  const mainOptionsSlideOutAnimation = useSpring({
    /** add the padding from the surrounding sidebar to make sure it moves totally out of view */
    marginLeft: showSecondLayerInfo ? `-${containerWidth + 16}` : '0px',
    width: '100%',
    height: '100%',
    maxWidth: windowWidth < 768 ? '100%' : '384px',
    display: 'flex',
    background: 'white',
    immediate: true, /** No animation */
  });

  const secondLayerSlideOutAnimation = useSpring({
    marginLeft: showThirdLayerInfo ? `-${containerWidth + 16}` : '0px',
    width: '100%',
    height: '100%',
    maxWidth: windowWidth < 768 ? '100%' : '384px',
    display: 'flex',
    background: 'white',
    immediate: true, /** No animation */
  });

  // Add transitions for second layer
  const secondLayerTransitions = useTransition(!!showSecondLayerInfo, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 1 },
    config: { duration: 0 }, /** No animation */
  });

  // Add transitions for third layer (if needed in the future)
  const thirdLayerTransitions = useTransition(!!showThirdLayerInfo, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 1 },
    config: { duration: 0 }, /** No animation */
  });

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setShowSecondLayerInfo(true);

    // Fetch the saved workout details when clicked
    if (template?.id && currentUser?.accountCode) {
      dispatch(fetchSavedWorkoutDetail(currentUser.accountCode, template.id));
    }
  };

  const shimmerComponents = Array.from({ length: 10 }).map((_, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <TemplateShimmerContainer key={`shimmer-${index}`}>
      <TemplateShimmerText />
      <TemplateShimmerIcon />
    </TemplateShimmerContainer>
  ));

  return (
    <>
      <TitleContainer>
        <ExpandedTitlesContainer ref={expandedTitlesContainerHeightRef}>
          <HoverTextContainer>
            <TitleText
              onClick={() => {
                setShowThirdLayerInfo(false);
                setShowSecondLayerInfo(false);
                dispatch(setSecondarySideBarDataTag(null));
              }}
              fontWeight={700}
              fontSize='13px'
              maxWidth={sideBarContainerWidth - 82}
            >
              Program Library
            </TitleText>
          </HoverTextContainer>
          {!!showSecondLayerInfo && (
            <>
              <Text
                fontSize='13px'
                fontWeight={700}
                style={{
                  marginLeft: '5px',
                  marginRight: '5px',
                  display: 'inline-block',
                  transform: 'rotate(15deg)',
                }}
              >
                /
              </Text>
              <TitleText
                color='#BDBDBD'
                fontSize='13px'
                fontWeight={400}
                maxWidth={sideBarContainerWidth - 82}
              >
                {selectedTemplate?.programName}
              </TitleText>
            </>
          )}
        </ExpandedTitlesContainer>
        {!showSecondLayerInfo && !showThirdLayerInfo && (
        <IcomoonReact
          iconSet={iconSet}
          size={16}
          icon='filter2'
          color='#9E9E9E'
          style={{
            cursor: 'pointer',
            minWidth: '15px',
          }}
          onClick={() => setShowTagFilterDropdown(!showTagFilterDropdown)}
        />
        )}
      </TitleContainer>
      <AnimationWrapper>
        <ResizeObserver
          onResize={(rect) => {
            setContainerWidth(rect?.width);
          }}
        />
        <animated.div
          key='first-layer'
          style={{
            ...mainOptionsSlideOutAnimation,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Wrapper>
            <SearchAndIcon
              id='search-container'
              onClick={() => setIsSearchFocused(true)}
              isFocused={isSearchFocused}
            >
              <SearchIconContainer id='search-icon-container'>
                <IcomoonReact
                  iconSet={iconSet}
                  size={18}
                  icon='magnifying-glass-alt'
                  color='#9E9E9E'
                />
              </SearchIconContainer>
              <SearchContainer
                placeholder='Search for a program'
                id='search-container-input'
                value={searchTerm}
                onFocus={() => setIsSearchFocused(true)}
                onChange={(inputValue) => {
                  setSearchTerm(inputValue.target.value);
                }}
              />
            </SearchAndIcon>
            {showTagFilterDropdown && (
            <div ref={tagFilterRef}>
              <SelectBox
                options={tagOptions}
                placeholder='Select Filter'
                value={
                    tagSearchTerm
                      ? tagOptions.find((option) => option.id === tagSearchTerm)
                      : null
                    }
                onChangeFunction={(option) => {
                  setTagSearchTerm(option ? option.id : null); // <-- allow null
                }}
                title='Tag Filter'
                titleStyledProps={{ fontSize: '13px', fontWeight: 700 }}
                customStyles={customSelectStyles}
                styledProps={{ height: '' }}
                styledPropsInput={{
                  width: '100% !important',
                }}
              />
            </div>
            )}
            <MappedComponents>
              <ExerciseWrapper ref={exerciseWrapperRef}>
                {isAllExercisesLoading ? (
                  shimmerComponents
                ) : (
                  <Droppable
                    droppableId='templates-sidebar'
                    isDropDisabled
                    renderClone={(provided, snapshot, rubric) => {
                      // NOTE: We intentionally use useEffect in renderClone here despite it
                      // violating React rules. This provides smoother drag animations without
                      // visual flashing. The alternative setTimeout approach causes a noticeable
                      // white flash. This only executes during active drags, so the performance
                      // impact is negligible.
                      useEffect(() => {
                        if (snapshot.isDragging) {
                          dispatch(setIsCloneDragging(snapshot.isDragging));
                          setDraggingCloneIndex(rubric.source.index);
                        }
                      }, [snapshot.isDragging, rubric.source.index, snapshot.isDropAnimating]);

                      const template = searchedExercises[rubric.source.index];
                      if (!template) return null;

                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            border: '1px dashed #77A7FF',
                            borderRadius: '4px',
                            background: 'white',
                            opacity: 1,
                          }}
                        >
                          <ExerciseItemContainer>
                            <ExerciseNameAndIconWrapper>
                              <TBTooltip
                                toolTipBackgroundColor='white'
                                tooltipContainerRef={exerciseWrapperRef}
                                textToDisplay={template?.programName}
                                textToHover={template?.programName}
                                addHoverOpacity
                                fontSize='16px'
                                fontWeight={400}
                                toolTipFontSize='13px'
                                toolTipFontWeight={400}
                                textContainerPadding='0px'
                                textContainerMaxWidth={`${sideBarContainerWidth - 100}px`}
                              />
                            </ExerciseNameAndIconWrapper>
                          </ExerciseItemContainer>
                        </div>
                      );
                    }}
                    mode='virtual'
                  >
                    {(droppableProvided) => (
                      <DroppableDiv
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                      >
                        <List
                          height={Math.max(
                            200,
                            sideBarContainerHeight - 100 - expandedTitlesContainerHeight
                              - (showTagFilterDropdown ? tagFilterHeight + 16 : 0),
                          )}
                          itemCount={searchedExercises?.length || 0}
                          itemSize={40}
                          width='100%'
                          outerRef={droppableProvided.innerRef}
                          outerElementType={StyledListWrapper}
                          itemData={{
                            searchedExercises,
                            exerciseWrapperRef,
                            isCloneDragging,
                            draggingCloneIndex,
                            sideBarContainerWidth,
                            handleTemplateClick,
                          }}
                        >
                          {Item}
                        </List>
                        {droppableProvided.placeholder}
                      </DroppableDiv>
                    )}
                  </Droppable>
                )}
              </ExerciseWrapper>
            </MappedComponents>
          </Wrapper>
        </animated.div>
        {secondLayerTransitions.map(({ item, props }) => (
          item && (
            <animated.div
              key='second-layer'
              style={{
                ...props,
                ...secondLayerSlideOutAnimation,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <SavedWorkoutDetails
                workout={selectedSavedWorkout}
                isLoading={isLoadingSavedWorkout}
                containerWidth={sideBarContainerWidth}
              />
            </animated.div>
          )
        ))}
        {thirdLayerTransitions.map(({ item, props }) => (
          item && (
            <animated.div
              key='third-layer'
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                ...props,
              }}
            >
              {/* Third layer content (if needed in the future) */}
            </animated.div>
          )
        ))}
      </AnimationWrapper>
    </>
  );
};

TemplatesSideBarContent.propTypes = {
  windowWidth: PropTypes.number.isRequired,
  sideBarContainerHeight: PropTypes.number.isRequired,
  sideBarContainerWidth: PropTypes.number.isRequired,
};

export default React.memo(TemplatesSideBarContent);
